import {
  getV1CompaniesCompanyIdRoutingSettings,
  patchV1CompaniesCompanyIdRoutingSettings,
  RoutingSettings_Update,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';

import { RoutingSettings } from './models';

export default function useRoutingSettings() {
  const fetch = useCallback(async (companyId: string, departmentId: string | null) => {
    try {
      const response = await getV1CompaniesCompanyIdRoutingSettings({
        path: { 'company-id': companyId },
        ...(departmentId ? { query: { department_id: departmentId } } : {}),
      });

      return RoutingSettings.parse(response.data.data);
    } catch (error) {
      // A 404 means the backfill has not run for this company or department. Neither
      // verb creates a row, so a save would 404 too: the caller shows an empty state and
      // leaves Save disabled until the backfill runs. Any other error leaves the caller
      // with no idea what the stored values are.
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      try {
        connection.handleRequestError(
          error,
          t('error_messages.routing_settings.failed_to_fetch'),
        );
      } catch {
        // Error already handled (toast + Sentry)
      }

      return undefined;
    }
  }, []);

  const update = useCallback(async (companyId: string, body: RoutingSettings_Update) => {
    try {
      const response = await patchV1CompaniesCompanyIdRoutingSettings({
        path: { 'company-id': companyId },
        body,
      });

      return RoutingSettings.parse(response.data.data);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.routing_settings.failed_to_save'),
      );
      throw error;
    }
  }, []);

  return { fetch, update };
}
